import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  Typography
} from "@material-ui/core";
import Autocomplete from "@saleor/components/Autocomplete";
import MyTypography from "@saleor/components/MyTypography";
import {
  usePoUploadCustomerSettingsCreateMutation,
  usePoUploadSuggestionSearchSoldToLazyQuery
} from "@saleor/graphql";
import { mapEdgesToItems } from "@saleor/utils/maps";
import React, { FC, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    modal: {
      "& .MuiPaper-root": {
        maxWidth: 440,
        width: "100%",
        borderRadius: 16
      },
      "& .MuiDialogTitle-root": {
        padding: 24
      },
      "& .MuiDialogContent-root": {
        paddingTop: 5
      },
      "& .MuiDialogActions-root": {
        borderTop: 0,
        paddingBottom: 24,
        columnGap: 16
      },
      "& .MuiButton-root": {
        fontSize: "1.8rem"
      }
    },
    flex: {
      display: "flex"
    },
    button: {
      borderRadius: "4px",
      width: "200px",
      height: "42px",
      display: "flex",
      alignItems: "center",
      alignSelf: "end",
      marginBottom: "1rem",
      padding: "16px 22px 16px 22px",
      justifyContent: "space-around",
      backgroundColor: theme.palette.info.main
    },
    buttonTitle: {
      color: "white",
      fontSize: "18px",
      paddingLeft: "12px"
    },
    btnAction: {
      height: 48,
      "&.MuiButton-containedPrimary .MuiTypography-root": {
        color: "#ffffff"
      }
    },
    title: {
      color: theme.palette.grey[900]
    }
  }),
  { name: "POSettingsAddButton" }
);

interface POSettingsAddButtonProps {
  refetch?: () => void;
}

const POSettingsAddButton: FC<POSettingsAddButtonProps> = ({ refetch }) => {
  const intl = useIntl();
  const classes = useStyles();
  const [value, setValue] = useState("");
  const [soldToId, setSoldToId] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [createMutation] = usePoUploadCustomerSettingsCreateMutation({
    onCompleted: () => {
      setIsAddModalOpen(false);
      refetch();
    }
  });
  const handleCreate = () => {
    createMutation({
      variables: {
        soldToId
      }
    });
  };

  const [
    getDataSuggestions,
    { data: fetchDataSuggestions, loading: loadingDataSuggestion }
  ] = usePoUploadSuggestionSearchSoldToLazyQuery();

  useEffect(() => {
    if (value?.length >= 2) {
      getDataSuggestions({
        variables: {
          first: 100,
          filter: {
            soldTo: value
          }
        }
      });
    }
  }, [value]);

  const dataOptions = useMemo(() => {
    const data =
      mapEdgesToItems(fetchDataSuggestions?.poUploadSuggestionSearchSoldTo) ||
      [];
    return data.map(data => ({
      title: `${data.soldToCode} - ${data.soldToName}`,
      value: data.id
    }));
  }, [fetchDataSuggestions?.poUploadSuggestionSearchSoldTo]);

  const getValueFromOptionTitle = title =>
    dataOptions.find(x => x.title === title)?.value;

  return (
    <>
      <Button
        className={classes.button}
        onClick={() => setIsAddModalOpen(true)}
      >
        <Typography className={classes.buttonTitle}>
          {intl.formatMessage({
            defaultMessage: "Add Customer"
          })}
        </Typography>
      </Button>

      <Dialog
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        className={classes.modal}
      >
        <DialogTitle>
          <MyTypography variant="h6" className={classes.title}>
            <FormattedMessage defaultMessage="เพิ่ม Customer Mapping" />
          </MyTypography>
        </DialogTitle>
        <DialogContent>
          <MyTypography variant="body2">
            <div className={classes.flex}>
              <MyTypography variant={"subtitle3"}>Sold to</MyTypography>
            </div>
            <Autocomplete
              resetAlways
              optionsProp={dataOptions}
              placeholderProps={intl.formatMessage({
                defaultMessage: "ค้นหา Sold to...",
                description: "label"
              })}
              onChangeProps={value => (value ? setValue(value) : null)}
              loading={loadingDataSuggestion}
              onSearch={value => {
                setValue(value);
                setSoldToId(getValueFromOptionTitle(value));
              }}
            />
          </MyTypography>
        </DialogContent>
        <DialogActions disableSpacing={true}>
          <Button
            color="secondary"
            variant="outlined"
            className={classes.btnAction}
            fullWidth={true}
            onClick={() => setIsAddModalOpen(false)}
          >
            <MyTypography variant="body1">
              <FormattedMessage defaultMessage="ยกเลิก" />
            </MyTypography>
          </Button>
          <Button
            color="secondary"
            variant="contained"
            className={classes.btnAction}
            fullWidth={true}
            disabled={!!!soldToId}
            onClick={handleCreate}
          >
            <MyTypography variant="body1">
              <FormattedMessage defaultMessage="ยืนยัน" />
            </MyTypography>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default POSettingsAddButton;
